// Reusable hook for initializing media (music and SFX) based on route and game context

import { useEffect, useRef } from 'react';
import { useRoute } from './useRoute';
import { useRoomConfig } from './useRoomConfig';
import { useRoomSocket } from './useRoomSocket';
import { createLogger, delay } from '@airhead-games/shared/utilities';
import type { SongMetadata } from '@airhead-games/shared/utilities';

const logger = createLogger('useMediaInitialization');

interface UseMediaInitializationProps {
  createMusicPlaylist: (criteria: Partial<SongMetadata>) => Promise<number>;
  requestGameSfx: (gameId: string) => Promise<void>;
  requestGameScene: (gameId: string) => Promise<void>;
}

export const useMediaInitialization = ({
  createMusicPlaylist,
  requestGameSfx,
  requestGameScene,
}: UseMediaInitializationProps) => {
  const { waitForConnectionReady } = useRoomSocket();
  const { isRoomRoute, isGameRoute, isHostRoute } = useRoute();
  const { gameId, roomCode } = useRoomConfig();

  const roomMusicLoaded = useRef(false);
  const gameMusicLoaded = useRef(false);
  const sfxLoaded = useRef(false);
  const scenesLoaded = useRef(false);

  // Reset flags when room or game context changes
  useEffect(() => {
    roomMusicLoaded.current = false;
    gameMusicLoaded.current = false;
    sfxLoaded.current = false;
    scenesLoaded.current = false;
  }, [roomCode, gameId, isHostRoute]);

  // Initialize all media sequentially
  useEffect(() => {
    let isCancelled = false;

    const initializeMedia = async () => {
      try {
        // Wait for WebSocket connection if we're the host (we'll be sending REQUEST commands)
        // During room teardown/navigation, host route state can briefly persist while
        // the socket has already been disconnected. Skip initialization unless we still
        // have an active room context.
        if (isHostRoute && roomCode) {
          await waitForConnectionReady();
        }

        if (isCancelled) return;

        // 1. Initialize music playlist
        if (!roomMusicLoaded.current && isRoomRoute && roomCode) {
          logger.debug('Initializing room music...');
          await createMusicPlaylist({ game: 'none' });
          roomMusicLoaded.current = true;
        } else if (!gameMusicLoaded.current && isGameRoute && gameId) {
          logger.debug(`Initializing game music for ${gameId}...`);
          await createMusicPlaylist({ game: gameId });
          gameMusicLoaded.current = true;
        }

        // Wait 2s for any potential MediaReady events being replayed to repopulate caches
        await delay(2000);

        if (isCancelled) return;

        // 2. Load SFX
        if (!sfxLoaded.current && gameId && isHostRoute) {
          await requestGameSfx(gameId);
          sfxLoaded.current = true;
        }

        // 3. Load scenes
        if (!scenesLoaded.current && gameId && isHostRoute) {
          await requestGameScene(gameId);
          scenesLoaded.current = true;
        }
      } catch (error) {
        if (isCancelled) return;
        logger.error('Failed to initialize media:', error);
      }
    };

    void initializeMedia();

    return () => {
      isCancelled = true;
    };
  }, [
    createMusicPlaylist,
    requestGameSfx,
    requestGameScene,
    waitForConnectionReady,
    isRoomRoute,
    isGameRoute,
    isHostRoute,
    gameId,
    roomCode,
  ]);
};
